import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  AfterViewInit,
  ViewChild,
  OnChanges,
  SimpleChanges,
  TemplateRef,
} from "@angular/core";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { Observable } from "rxjs";

export interface GridColumn {
  key: string;
  label: string;
  sortable?: boolean;
  filterable?: boolean;
  width?: string;
  type?: "text" | "number" | "date" | "custom";
  customTemplate?: TemplateRef<any>;
  accessor?: (item: any) => any; // For nested properties
}

export interface GridAction {
  label: string;
  icon?: string;
  color?: "primary" | "accent" | "warn";
  tooltip?: string;
  visible?: (item: any) => boolean;
  disabled?: (item: any) => boolean;
}

export interface GridConfig {
  pageSize?: number;
  pageSizeOptions?: number[];
  showFirstLastButtons?: boolean;
  showSearch?: boolean;
  showColumnFilters?: boolean;
  enableSorting?: boolean;
  stickyHeader?: boolean;
}

@Component({
    selector: "app-data-grid",
    templateUrl: "./data-grid.component.html",
    styleUrls: ["./data-grid.component.css"],
    standalone: false
})
export class DataGridComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() title?: string;
  @Input() data: any[] = [];
  @Input() columns: GridColumn[] = [];
  @Input() actions: GridAction[] = [];
  @Input() config: GridConfig = {};
  @Input() loading = false;
  @Input() error: string | null = null;
  @Input() totalCount?: number;
  @Input() trackByFn?: (index: number, item: any) => any;

  @Output() actionClicked = new EventEmitter<{
    action: GridAction;
    item: any;
    event: Event;
  }>();
  @Output() searchChanged = new EventEmitter<string>();
  @Output() columnFilterChanged = new EventEmitter<{
    column: string;
    value: string;
  }>();
  @Output() sortChanged = new EventEmitter<{
    column: string;
    direction: "asc" | "desc" | "";
  }>();
  @Output() pageChanged = new EventEmitter<{
    pageIndex: number;
    pageSize: number;
  }>();
  @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort!: MatSort;
  dataSource = new MatTableDataSource<any>([]);
  displayedColumns: string[] = [];
  filterColumns: string[] = [];
  globalSearchValue = "";
  columnFilters: { [key: string]: string } = {};

  // Default configuration
  defaultConfig: GridConfig = {
    pageSize: 25,
    pageSizeOptions: [5, 10, 25, 50, 100],
    showFirstLastButtons: true,
    showSearch: true,
    showColumnFilters: true,
    enableSorting: true,
    stickyHeader: true,
  };
  ngOnInit(): void {
    this.config = { ...this.defaultConfig, ...this.config };
    this.setupColumns();
    this.setupDataSource();
    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["data"] && !changes["data"].firstChange) {
      this.loadData();
    }
    if (changes["columns"] && !changes["columns"].firstChange) {
      this.setupColumns();
    }
    if (changes["config"] && !changes["config"].firstChange) {
      this.config = { ...this.defaultConfig, ...this.config };
      this.setupColumns();
      this.setupDataSource();
    }
  }
  ngAfterViewInit(): void {
    // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.connectPaginatorAndSort();
    }, 0);
  }

  private setupColumns(): void {
    this.displayedColumns = this.columns.map((col) => col.key);
    if (this.actions && this.actions.length > 0) {
      this.displayedColumns.push("actions");
    }

    // Setup filter columns if column filtering is enabled
    if (this.config.showColumnFilters) {
      this.filterColumns = this.columns.map((col) => `${col.key}-filter`);
      if (this.actions && this.actions.length > 0) {
        this.filterColumns.push("actions-filter");
      }
    }
  }

  private setupDataSource(): void {
    if (this.config.showColumnFilters) {
      this.setupColumnFilterPredicate();
    } else {
      this.setupGlobalFilterPredicate();
    }

    this.setupSortAccessor();
  }

  private setupGlobalFilterPredicate(): void {
    this.dataSource.filterPredicate = (data: any, filter: string) => {
      // Always show the filter row
      if (data.isFilterRow) {
        return true;
      }

      if (!filter || !filter.trim()) {
        return true;
      }

      // Create search string from all filterable columns
      const searchStr = this.columns
        .filter((col) => col.filterable !== false)
        .map((col) => this.getColumnValue(data, col))
        .join(" ")
        .toLowerCase();

      return searchStr.includes(filter.toLowerCase());
    };
  }

  private setupColumnFilterPredicate(): void {
    this.dataSource.filterPredicate = (data: any, filter: string) => {
      // Always show the filter row
      if (data.isFilterRow) {
        return true;
      }

      // Check if any column filters are active
      const hasActiveFilters = Object.values(this.columnFilters).some(
        (filterValue) => filterValue && filterValue.trim()
      );

      if (!hasActiveFilters) {
        return true;
      }

      // Apply column-specific filters - ALL must pass
      for (const column in this.columnFilters) {
        const columnFilter = this.columnFilters[column];
        if (columnFilter && columnFilter.trim()) {
          const columnConfig = this.columns.find((col) => col.key === column);
          if (columnConfig) {
            const fieldValue = this.getColumnValue(
              data,
              columnConfig
            ).toLowerCase();
            if (!fieldValue.includes(columnFilter.toLowerCase())) {
              return false;
            }
          }
        }
      }

      return true;
    };
  }

  private setupSortAccessor(): void {
    this.dataSource.sortingDataAccessor = (item, property) => {
      if (item.isFilterRow) return "";

      const column = this.columns.find((col) => col.key === property);
      if (column) {
        const value = this.getColumnValue(item, column);
        return value?.toString().toLowerCase() || "";
      }
      return "";
    };

    // Custom sort to keep filter row at top
    this.dataSource.sortData = (data: any[], sort: MatSort) => {
      if (!sort.active || sort.direction === "") {
        return data;
      }

      const filterRow = data.find((item) => item.isFilterRow);
      const dataRows = data.filter((item) => !item.isFilterRow);

      const sortedDataRows = dataRows.sort((a, b) => {
        const isAsc = sort.direction === "asc";
        const valueA = this.dataSource.sortingDataAccessor(a, sort.active);
        const valueB = this.dataSource.sortingDataAccessor(b, sort.active);
        return this.compare(valueA, valueB, isAsc);
      });

      return filterRow ? [filterRow, ...sortedDataRows] : sortedDataRows;
    };
  }

  private loadData(): void {
    let processedData = [...this.data];

    // Add filter row if column filtering is enabled
    if (this.config.showColumnFilters) {
      const filterRow = { isFilterRow: true } as any;
      processedData = [filterRow, ...processedData];
    }

    this.dataSource.data = processedData;

    setTimeout(() => {
      this.connectPaginatorAndSort();
    });
  }
  private connectPaginatorAndSort(): void {
    if (this.paginator) {
      // Custom connect for paginator to handle filter row
      this.dataSource.paginator = this.paginator;

      // Override the paginator's connect method to always include filter row
      if (this.config.showColumnFilters) {
        this.setupCustomPagination();
      }
    }
    if (this.sort && this.config.enableSorting) {
      this.dataSource.sort = this.sort;
    }
  }

  private setupCustomPagination(): void {
    // Store original _filterData method
    const originalFilterData = this.dataSource._filterData.bind(
      this.dataSource
    );

    // Override _filterData to always include filter row at top
    this.dataSource._filterData = (data: any[]) => {
      const filteredData = originalFilterData(data);

      // Separate filter row from data rows
      const filterRow = filteredData.find((item) => item.isFilterRow);
      const dataRows = filteredData.filter((item) => !item.isFilterRow);

      // Return filter row + data rows
      return filterRow ? [filterRow, ...dataRows] : dataRows;
    };

    // Store original _pageData method
    const originalPageData = this.dataSource._pageData.bind(this.dataSource);

    // Override _pageData to always include filter row at top
    this.dataSource._pageData = (data: any[]) => {
      // Separate filter row from data rows
      const filterRow = data.find((item) => item.isFilterRow);
      const dataRows = data.filter((item) => !item.isFilterRow);

      // Apply pagination only to data rows
      const pagedDataRows = originalPageData(dataRows);

      // Return filter row + paged data rows
      return filterRow ? [filterRow, ...pagedDataRows] : pagedDataRows;
    };

    // Override _updatePaginator to correctly set the length for pagination
    const originalUpdatePaginator = this.dataSource._updatePaginator.bind(
      this.dataSource
    );
    this.dataSource._updatePaginator = (filteredDataLength: number) => {
      // Subtract 1 from length if filter row is present
      const hasFilterRow = this.dataSource.filteredData.some(
        (item) => item.isFilterRow
      );
      const adjustedLength = hasFilterRow
        ? filteredDataLength - 1
        : filteredDataLength;
      originalUpdatePaginator(adjustedLength);
    };
  }
  onGlobalSearch(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.globalSearchValue = filterValue;

    // Clear column filters when using global search
    if (filterValue.trim()) {
      this.clearColumnFilters();
    }

    // Setup the appropriate filter predicate
    this.setupGlobalFilterPredicate();
    this.dataSource.filter = filterValue.trim().toLowerCase();
    this.searchChanged.emit(filterValue);

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }
  onColumnFilterChange(column: string, value: string): void {
    const trimmedValue = value ? value.trim().toLowerCase() : "";

    if (trimmedValue) {
      this.columnFilters[column] = trimmedValue;
    } else {
      delete this.columnFilters[column];
    }

    // Clear global search when using column filters
    this.globalSearchValue = "";

    // Setup the appropriate filter predicate
    this.setupColumnFilterPredicate();
    this.updateColumnFilters();
    this.columnFilterChanged.emit({ column, value });
  }

  onColumnFilter(column: string, event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.onColumnFilterChange(column, filterValue);
  }
  private updateColumnFilters(): void {
    // Check if any column filters are active
    const hasActiveFilters = Object.values(this.columnFilters).some(
      (filterValue) => filterValue && filterValue.trim()
    );

    if (!hasActiveFilters) {
      // If no filters are active, reset to show all data
      this.dataSource.filter = " ";
      this.dataSource.filter = "";
    } else {
      // Trigger filter update
      this.dataSource.filter = "column-filter-update";
    }

    // Reset to first page when filters change, but account for filter row
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }
  clearColumnFilters(): void {
    this.columnFilters = {};

    // Reset filter to trigger the predicate with empty filter
    this.dataSource.filter = " ";
    this.dataSource.filter = "";
  }
  clearGlobalSearch(): void {
    this.globalSearchValue = "";

    // If column filtering is enabled, switch back to column filter predicate
    if (this.config.showColumnFilters) {
      this.setupColumnFilterPredicate();
    }

    this.dataSource.filter = "";
  }

  onActionClick(action: GridAction, item: any, event: Event): void {
    event.stopPropagation();
    this.actionClicked.emit({ action, item, event });
  }

  onPageChange(event: any): void {
    this.pageChanged.emit({
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
    });
  }

  getColumnValue(item: any, column: GridColumn): any {
    if (column.accessor) {
      return column.accessor(item);
    }

    // Handle nested properties (e.g., 'plant.name')
    const keys = column.key.split(".");
    let value = item;
    for (const key of keys) {
      value = value?.[key];
    }
    return value || "";
  }

  isActionVisible(action: GridAction, item: any): boolean {
    if (item.isFilterRow) return false;
    return action.visible ? action.visible(item) : true;
  }

  isActionDisabled(action: GridAction, item: any): boolean {
    return action.disabled ? action.disabled(item) : false;
  }

  get totalItems(): number {
    if (this.totalCount !== undefined) {
      return this.totalCount;
    }

    // Get the original data count excluding filter row
    const originalData = this.data || [];
    return originalData.length;
  }

  // Row predicate functions
  isFilterRow = (index: number, item: any) => item.isFilterRow;
  isDataRow = (index: number, item: any) => !item.isFilterRow;
  trackByItem(index: number, item: any): any {
    if (this.trackByFn) {
      return this.trackByFn(index, item);
    }
    return item.isFilterRow ? -1 : item.id || index;
  }

  getBootstrapButtonClass(color?: string): string {
    switch (color) {
      case "primary":
        return "btn btn-primary";
      case "accent":
        return "btn btn-info";
      case "warn":
        return "btn btn-warning";
      default:
        return "btn btn-primary";
    }
  }

  private compare(
    a: string | number,
    b: string | number,
    isAsc: boolean
  ): number {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }
}
